import { Loader2 } from "lucide-react";
import { createPortal } from "react-dom";
import { useIsMobile } from '@/core/hooks/useMediaQuery';


interface MergingConfirmDialogProps {
  primaryEmail: string;
  isSubmitting: boolean;
  onConfirm: () => void;
  onBack: () => void;
}

export function MergingConfirmDialog({
  primaryEmail,
  isSubmitting,
  onConfirm,
  onBack,
}: MergingConfirmDialogProps) {
  const isMobile = useIsMobile();

  return createPortal(
    <div
      className={`fixed inset-0 z-[60] bg-black/40 flex justify-center ${
        isMobile ? "items-end" : "items-center"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
          onBack();
        }
      }}
    >
      <div
        className={`bg-white w-full flex flex-col shadow-xl transition-all transform ${
          isMobile
            ? "rounded-t-2xl p-[20px] animate-in slide-in-from-bottom-4 duration-300"
            : "max-w-lg rounded-lg p-6 mx-4"
        }`}
      >
        <div className="flex flex-col justify-between items-start mb-2">
          <p className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>
            Confirm changes
          </p>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          You are about to update your accounts linked to this phone number.
        </p>

        <div className="bg-gray-100 p-3 mb-4 flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium tracking-wider">
            Primary Account
          </span>
          <span className="text-sm font-semibold text-black break-all">
            {primaryEmail}
          </span>
        </div>

        <p className="text-[13px] text-red-600 mb-4">
          We'll apply the merge/transfer actions you selected. This can't be undone.
        </p>

        <div className={`flex gap-3 justify-end`}>
          <button
            onClick={onBack}
            disabled={isSubmitting}
            className={`border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors ${isMobile ? 'w-[100px] p-[14px]' : 'p-[12px] w-fit'}`}
          >
            Back
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className={`bg-hover text-white font-medium disabled:opacity-50 flex items-center justify-center transition-colors ${isMobile ? 'w-full p-[14px]' : 'p-[12px] w-full'}`}
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}